import { useId, useState } from 'react'
import { Chip } from '../../components/ui/Chip'
import type { components } from '../../lib/api/schema.gen'
import type { QuoteVariant } from '../rfq/domain'
import { RFQ_QUOTE_VARIANTS } from '../rfq/domain'
import { qualitativeVariantLabels } from '../rfq/quote-variant-labels'
import { REGION_GROUPS } from '../rfq/region-groups'
import { deliveryOptionForVariant, variantForDeliveryOption } from '../rfq/variant-pricing'
import { formatMoney } from './cart-money'
import { computeCartQuoteOptionPreview } from './cart-quote-option-pricing'

type LineItem = components['schemas']['LineItem']
type DeliveryOption = components['schemas']['DeliveryOption']
type CartDeliveryOptionTotalEntry = components['schemas']['CartDeliveryOptionTotalEntry']

export interface CartQuoteOptionSelectorProps {
  /** The cart's lines — used ONLY to count the lines EXCLUDED from pricing
   * (`computeCartQuoteOptionPreview`'s `excludedLineCount`, which drives the
   * "Excludes N item(s) priced separately" caption); never mutated.
   *
   * UID-622 (ADR-0181 D4) — the per-variant ratio-scaled totals this used to
   * also produce are no longer rendered: the cart server never ratio-scales
   * part prices after cart-commit, so a "Parts $X /ea" sub-label advertised a
   * difference the cart will never charge. */
  readonly lines: readonly LineItem[]
  /** UID-622 (ADR-0181 D1/D4) — RETAINED but now only a FALLBACK. Every figure this
   * component renders is server-authored and arrives with its own currency (the
   * `deliveryOptionTotals` entry's and the `DeliveryOption`'s); this is the cart-level
   * currency used when a figure somehow carries none, so a display can never silently
   * render an amount with the wrong symbol. */
  readonly currency: string
  readonly disabled?: boolean
  /** UID-456 — the cart's real, server-priced delivery options (the SAME array
   * `DeliveryOptionCard` used to render). Each row's headline price/savings is
   * resolved from here via `deliveryOptionForVariant`, not from the parts-only
   * ratio preview — the two are different numbers (shipping cost vs. part-price
   * region ratio) and conflating them was the UID-440 regression this fixes. */
  readonly deliveryOptions: readonly DeliveryOption[]
  /** UID-622 (ADR-0181 D1/D2) — the SERVER-derived order total per delivery
   * option (`CartViewWithLineSpecifications.deliveryOptionTotals`), keyed by
   * `deliveryOptionId`. This is the row's HEADLINE figure: the number the buyer
   * is actually choosing between (parts + charges − discount + that option's
   * shipping + tax), which is what the Order Summary will show once the row is
   * selected. Never recomputed here — the money chain lives on the server
   * (ADR-0029; the client-side recompute was rejected in ADR-0181 D2 because the
   * discount base is catalog-only and cent-rounded before tax, so a second
   * implementation would drift from checkout). An option with no entry renders a
   * caption, NEVER its bare shipping charge — showing a shipping charge where a
   * total belongs is precisely the defect UID-622 closes. */
  readonly deliveryOptionTotals: readonly CartDeliveryOptionTotalEntry[]
  /** UID-456 — the cart's real, persisted delivery-option selection
   * (`shipPlan.selectedDeliveryOptionId`, ship-together mode only; `null`
   * otherwise/when unset). Used to initialize the default-checked row via
   * {@link variantForDeliveryOption} instead of always defaulting to the
   * first canonical variant — the other half of that same regression. */
  readonly selectedDeliveryOptionId: string | null
  /** UID-440 (cart follow-up) — the SAME delivery-option mutation
   * `ShipPlanCard`/`DeliveryOptionCard` already call
   * (`PUT /cart/delivery-option` via `CartPage.handleSelectDeliveryOption`).
   * This component owns no endpoint of its own: selecting a variant maps to
   * a delivery-option id via `deliveryOptionForVariant` and forwards it
   * here, exactly the same round trip a raw delivery-option click already
   * made. */
  readonly onSelectDeliveryOption: (deliveryOptionId: string) => void
}

/**
 * UID-440 (cart follow-up) — the Cart's `ship_together` region/quote-option
 * selector, REPLACING the old 3-card `.delivery-option-grid`
 * (expedited/standard/overseas) with the RFQ-style 4-variant, 2-column
 * North America / Overseas picker.
 *
 * Deliberately NOT `QuoteVariantSelector` (rfq/QuoteVariantSelector.tsx) —
 * that component's framing copy is hardcoded non-injectable post-quote
 * copy, and ADR-0104 D1 scopes its mint(#EEFFF9)/checkmark paint to RFQ
 * only. This is a cart-scoped FORK of the same anatomy (bold lead + muted
 * sublabel + right-aligned "$X /ea" row, one box per region column) with
 * cart-appropriate copy and the cart's OWN existing selected-state paint
 * (`--color-brand-primary` border accent + `--color-brand-tint-50` tint —
 * the same tokens `.delivery-option-card-checked`/`.radio-card-input`
 * already use elsewhere in this file; see `cart.css` for the rule-by-rule
 * comparison). Vocabulary/logic (region grouping, the ratio math) is
 * SHARED with RFQ via `../rfq/region-groups` and `../rfq/variant-pricing` —
 * only the component and its paint are forked.
 *
 * This component owns its own `selectedVariantId` (client-only, cart state),
 * initialized from the cart's REAL persisted `selectedDeliveryOptionId` via
 * {@link variantForDeliveryOption} (UID-456 — safe now that every variant has
 * its own distinct delivery-option id; previously `offshore` AND
 * `offshore_economy` both mapped to `do-overseas`, making that reverse lookup
 * ambiguous, so this used to always default to the first canonical variant
 * regardless of what the cart had actually persisted). Still falls back to
 * the first canonical variant (`us`) only when there is genuinely no
 * persisted selection yet (a fresh cart) or the id is unrecognized.
 */
export function CartQuoteOptionSelector({
  lines,
  currency,
  disabled = false,
  // UID-622 (cart follow-up) — no longer read: the row's sub-label is now the qualitative
  // Fastest/Standard/Cost-effective label (`labelsByVariantId`, below), not the
  // shipping/savings figures this array used to feed. Kept in the prop contract because
  // `CartPage` still passes the same array `DeliveryOptionCard` renders — removing the
  // PROP would be a needless API churn for a display-only change.
  deliveryOptionTotals,
  selectedDeliveryOptionId,
  onSelectDeliveryOption,
}: CartQuoteOptionSelectorProps) {
  const baseId = useId()
  const [selectedVariantId, setSelectedVariantId] = useState<QuoteVariant['id']>(
    () => variantForDeliveryOption(selectedDeliveryOptionId) ?? RFQ_QUOTE_VARIANTS[0]?.id ?? 'us',
  )

  // UID-622 (ADR-0181 D4) — only `excludedLineCount` is read now. The per-variant
  // ratio-scaled parts totals are deliberately unused: see the `lines` prop doc.
  const { excludedLineCount } = computeCartQuoteOptionPreview(lines, RFQ_QUOTE_VARIANTS)

  // UID-622 (cart follow-up) — the qualitative Fastest/Standard/Cost-effective label per
  // variant, ranked by lead time ACROSS ALL 4 variants (both region columns together), via
  // the shared `../rfq/quote-variant-labels` utility — never derived per-group, which would
  // award "Fastest" independently within each column instead of across the full option set.
  const labelsByVariantId = qualitativeVariantLabels(RFQ_QUOTE_VARIANTS)

  function handleSelect(variantId: QuoteVariant['id']) {
    setSelectedVariantId(variantId)
    const deliveryOptionId = deliveryOptionForVariant(variantId)
    if (deliveryOptionId !== undefined) {
      onSelectDeliveryOption(deliveryOptionId)
    }
  }

  return (
    <div className="cart-quote-option-selector">
      <div className="cart-quote-option-framing">
        <h2 className="cart-quote-option-heading">Choose how this order ships</h2>
        {/* Cart copy deliberately does NOT reuse the RFQ selector's green
         * "All parts priced" success chip (ADR-0100 D1-adjacent concern) —
         * the cart cannot assert every line is priced (unpriced/pending
         * lines are a normal cart state), so a neutral "Preview pricing"
         * chip is used instead, never a false readiness claim. */}
        <Chip tone="info" className="cart-quote-option-preview-chip">
          Preview pricing
        </Chip>
      </div>
      <p className="cart-quote-option-subcopy">
        Prices update instantly as a preview — your final total is confirmed at checkout.
      </p>
      <div role="radiogroup" aria-label="Ship option" className="cart-quote-option-groups">
        {REGION_GROUPS.map((group) => {
          const groupTitleId = `${baseId}-group-${group.key}-title`
          const groupVariants = group.variantIds
            .map((id) => RFQ_QUOTE_VARIANTS.find((variant) => variant.id === id))
            .filter((variant): variant is QuoteVariant => variant !== undefined)

          if (groupVariants.length === 0) {
            return null
          }

          return (
            <div
              key={group.key}
              className="cart-quote-option-group"
              // Supplementary AT grouping nested inside the outer
              // radiogroup — same non-`<fieldset>` rationale as
              // `QuoteVariantSelector.tsx`'s identical structure.
              // biome-ignore lint/a11y/useSemanticElements: see comment above.
              role="group"
              aria-labelledby={groupTitleId}
            >
              <h3 id={groupTitleId} className="cart-quote-option-group-title">
                {group.title}
              </h3>
              <div className="cart-quote-option-group-box">
                {groupVariants.map((variant, index) => {
                  const checked = variant.id === selectedVariantId
                  const inputId = `${baseId}-${variant.id}`
                  // UID-622 (ADR-0181 D1) — the row's HEADLINE figure is the SERVER-DERIVED
                  // ORDER TOTAL for the delivery option this variant maps to: the number the
                  // buyer is choosing between, and exactly what the Order Summary will show
                  // once this row is selected. SUPERSEDES UID-456, which put the option's
                  // bare shipping charge here (right that shipping and the parts-only ratio
                  // preview are different numbers; wrong that shipping was therefore the
                  // headline). Shipping + savings-vs-fastest survive in the sub-label below,
                  // where they are still true and still useful.
                  const deliveryOptionId = deliveryOptionForVariant(variant.id)
                  const matchedTotal = deliveryOptionTotals.find(
                    (t) => t.deliveryOptionId === deliveryOptionId,
                  )
                  const classes = [
                    'cart-quote-option-row',
                    checked && 'cart-quote-option-row-checked',
                    // Hairline divider between rows within the box — not on
                    // the first row (no divider above the box's own top
                    // edge/border).
                    index > 0 && 'cart-quote-option-row-divider',
                  ]
                    .filter(Boolean)
                    .join(' ')

                  return (
                    <label key={variant.id} htmlFor={inputId} className={classes}>
                      <input
                        id={inputId}
                        type="radio"
                        name={`${baseId}-cart-quote-option`}
                        value={variant.id}
                        checked={checked}
                        disabled={disabled}
                        aria-describedby={groupTitleId}
                        onChange={() => handleSelect(variant.id)}
                        className="cart-quote-option-radio"
                      />
                      <span className="cart-quote-option-body">
                        <span className="cart-quote-option-lead">{variant.leadTimeLabel}</span>
                        {group.sublabel !== undefined && (
                          <span className="cart-quote-option-sublabel">{group.sublabel}</span>
                        )}
                        {/* UID-622 (ADR-0181 D4) — the ratio-scaled "Parts $X /ea"
                         * sub-label that used to sit here is REMOVED, not relabeled. The
                         * cart server never ratio-scales part prices after cart-commit
                         * (the RFQ ratio is baked into the line price once; changing the
                         * delivery option moves SHIPPING only), so the line advertised a
                         * part-price difference the cart will never charge — and it would
                         * now visibly contradict the real total in the headline. It also
                         * mislabeled a SUMMED figure with a per-unit "/ea" suffix, the
                         * same defect UID-607 already fixed on the RFQ surface. */}
                      </span>
                      <span className="cart-quote-option-price">
                        {matchedTotal ? (
                          <>
                            {formatMoney(
                              matchedTotal.orderTotalAmount,
                              matchedTotal.currency || currency,
                            )}
                            {/* UID-622 (cart follow-up) — REPLACES the shipping-cost /
                             * "Save $X" sub-label that used to sit here. Removed per direct
                             * product request after reviewing the merged UID-622 fix: a
                             * qualitative Fastest/Standard/Cost-effective label instead,
                             * ranked by lead time ACROSS all 4 options (both region columns
                             * together — see `labelsByVariantId` above, built from
                             * `qualitativeVariantLabels(RFQ_QUOTE_VARIANTS)`, never from just
                             * this row's own group). */}
                            <span className="cart-quote-option-price-sub">
                              {labelsByVariantId.get(variant.id)}
                            </span>
                          </>
                        ) : (
                          /* No server-derived total for this option — a caption, NEVER the
                           * bare shipping charge standing in for a total (UID-622). */
                          <span className="caption-text">Total not available</span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      {excludedLineCount > 0 && (
        <p className="caption-text cart-quote-option-excluded-caption">
          Excludes {excludedLineCount} {excludedLineCount === 1 ? 'item' : 'items'} priced
          separately.
        </p>
      )}
    </div>
  )
}
